import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import { selectSingleBatchData } from '@reducers/batch';
import {
  getColorBasedOnMachineStatus,
  localTime,
  machineStatusName,
  machineStatus as machineStatusConstants,
  formatSinceTimeWithDayTranslationCase,
} from '../../../../../../../../../../../helpers';
import { Machine } from '@interfaces/machines/index.ts';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation.ts';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config.tsx';
import { BatchDetailsScreen, BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum.ts';
import { Perspective } from '@v2/pages/common/enums/index.ts';
import { getIconName, getStatusDescription, getShowExpectedEnd } from '@v2/pages/machines/modules/now/components/Overview/components/MachineActivityBlock/helpers/index.ts';
import { selectServerOffset } from '@reducers/time';
import moment from 'moment';
import { ELAPSED_TIME_BREAKPOINT, TIME_CONFIG } from '@v2/pages/machines/modules/now/components/Overview/constants/index.ts';
import { ProductionBatchStatistics } from '@interfaces/search/index.ts';
import { is24HourFormat } from '@components/frame.js';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces/index.ts';

export const useManageMachineActivityBlock = () => {
  const selectedMachine: Machine = useSelector(selectSelectedItem);
  const batchState: ReducerEntityWithLoadingState<ProductionBatchStatistics | null> = useSelector(selectSingleBatchData);
  const batch: ProductionBatchStatistics | null = batchState.data;
  const batchLoaded = selectedMachine?.machineStatus?.productionBatchId != null && batchState.wasLoaded && batch != null;

  const timeOffset = useSelector(selectServerOffset);
  const lastUpdatedOn = selectedMachine?.machineStatus?.lastUpdatedOn;

  const elapsedSeconds = useMemo(() => {
    const time = moment.utc().add(timeOffset, 'milliseconds');
    return time.diff(lastUpdatedOn ? moment.utc(lastUpdatedOn) : moment.utc(), 's');
  }, [lastUpdatedOn, timeOffset]);

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const machineStatus = selectedMachine?.machineStatus;
  const latestMachineEvent = selectedMachine?.latestMachineEvent;

  const status = machineStatus?.status;
  const startedOn = latestMachineEvent?.startedOn;

  const { cycleTime, targetDuration }: { cycleTime: number | null; targetDuration: number | null } = useMemo(() => {
    switch (machineStatus.status) {
      case machineStatusConstants.ACTIVE: {
        return {
          cycleTime: batch?.scheduledCycleTimeAverageInSeconds ?? 0,
          targetDuration: batch?.scheduledActiveTimeAverageInSeconds || batch?.actualActiveTimeAverageInSeconds || 0,
        };
      }
      case machineStatusConstants.CHANGE: {
        return {
          cycleTime: batch?.scheduledCycleTimeAverageInSeconds ?? 0,
          targetDuration: batch?.scheduledChangeTimeAverageInSeconds || batch?.actualChangeTimeAverageInSeconds || 0,
        };
      }
      case machineStatusConstants.SETUP: {
        return { cycleTime: null, targetDuration: batch?.scheduledSetupTimeInSeconds || batch?.actualSetupTimeInSeconds || 0 };
      }
      case machineStatusConstants.NOTPRODUCTIVE: {
        return { cycleTime: null, targetDuration: null };
      }
      default: {
        return { cycleTime: null, targetDuration: null };
      }
    }
  }, [
    batch?.scheduledCycleTimeAverageInSeconds,
    batch?.scheduledActiveTimeAverageInSeconds,
    batch?.scheduledChangeTimeAverageInSeconds,
    batch?.scheduledSetupTimeInSeconds,
    machineStatus.status,
  ]);

  const targetDurationExist = targetDuration != null;
  const shouldRunElapsedSeconds = targetDurationExist || elapsedSeconds < ELAPSED_TIME_BREAKPOINT;
  const sinceTime = formatSinceTimeWithDayTranslationCase(lastUpdatedOn, is24HourFormat);
  const shouldDisplaySinceTime = !targetDurationExist && !shouldRunElapsedSeconds;

  const statusColor = useMemo(() => {
    if (!status) return 'Status/Light/Active';
    return getColorBasedOnMachineStatus(status);
  }, [status]);

  const statusName = useMemo(() => (status ? machineStatusName[status] ?? status : ''), [status]);
  const iconName = useMemo(() => getIconName(status), [status]);
  const statusDescription = useMemo(
    () => getStatusDescription(status, machineStatus?.subStatus, machineStatus?.executionMode),
    [status, machineStatus?.subStatus, machineStatus?.executionMode]
  );
  const showExpectedEnd = useMemo(() => getShowExpectedEnd(status), [status]);

  const progress = useMemo(() => {
    if (!targetDuration || targetDuration <= 0) return 0;
    return Math.min(elapsedSeconds / targetDuration, 1);
  }, [elapsedSeconds, targetDuration]);

  const { format, placeholder } = TIME_CONFIG;

  const startedTime = startedOn ? localTime(startedOn).format(format) : placeholder;
  const expectedEnd = useMemo(() => {
    if (!startedOn || !targetDuration) return placeholder;
    return localTime(startedOn).add(targetDuration, 'seconds').format(format);
  }, [startedOn, targetDuration]);

  const handlePress = useCallback(() => {
    const batchNumber = batch?.batchNumber;
    const batchId = machineStatus?.productionBatchId;
    const ncFileStatus = machineStatus?.ncFileStatus;

    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      levelSubScreen: BatchLevelSubScreen.TIMES,
      params: {
        perspective: Perspective.BATCH,
        batchNumber,
        batchId,
        ncFileStatus,
      },
    });
  }, [batch?.batchNumber, machineStatus?.productionBatchId, machineStatus?.ncFileStatus, navigateToMainDetailsScreen]);

  return {
    machineStatus,
    statusColor,
    statusName,
    iconName,
    statusDescription,
    showExpectedEnd,
    progress,
    startedTime,
    expectedEnd,
    targetDuration,
    shouldRunElapsedSeconds,
    shouldDisplaySinceTime,
    sinceTime,
    cycleTime,
    elapsedSeconds,
    handlePress,
    batch,
    batchLoaded,
  };
};
